import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { salesAPI } from '../lib/api';
import { cn } from '../lib/utils';
import {
  Calendar,
  ShoppingCart,
  Users,
  Package,
  Scissors,
  DollarSign,
  Receipt,
  PiggyBank,
  Gift,
  BarChart3,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Bell,
  Search,
  Beaker,
  User,
  Wrench,
  Briefcase,
  Fingerprint,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import ChangePinDialog from './ChangePinDialog';
import useUnsavedCashRegisterGuard from '../lib/useUnsavedCashRegisterGuard';

const BARRABAS_LOGO = "https://customer-assets.emergentagent.com/job_771f31db-11b6-4883-8e50-35de394374ea/artifacts/xf4slwx6_LOGO%20VB.jpg.jpeg";

// Navigation structure with hierarchy
const getNavStructure = (userRole, pendingAuthCount = 0) => {
  const administrativeChildren = [
    { icon: DollarSign, label: 'Gastos', path: '/gastos' },
    { icon: Receipt, label: 'Comisiones', path: '/comisiones' },
    { icon: Fingerprint, label: 'Asistencia', path: '/asistencia' },
    { icon: BarChart3, label: 'Reportes Financieros', path: '/reportes' },
    { icon: Sparkles, label: 'Análisis y tendencias', path: '/reportes/insights' },
  ];

  if (userRole === 'master_admin') {
    administrativeChildren.splice(3, 0, {
      icon: ShieldCheck,
      label: 'Autorizaciones',
      path: '/autorizaciones',
      badge: pendingAuthCount,
    });
  }

  const adminNav = [
    { 
      type: 'link',
      icon: Calendar, 
      label: 'Agenda', 
      path: '/agenda' 
    },
    { 
      type: 'dropdown',
      icon: ShoppingCart, 
      label: 'POS', 
      children: [
        { icon: PiggyBank, label: 'Abrir Caja', path: '/caja' },
        { icon: ShoppingCart, label: 'Punto de Venta', path: '/pos' },
      ]
    },
    { 
      type: 'link',
      icon: Users, 
      label: 'Clientes', 
      path: '/clientes' 
    },
    { 
      type: 'link',
      icon: Gift, 
      label: 'Lealtad', 
      path: '/lealtad' 
    },
    { 
      type: 'dropdown',
      icon: Wrench, 
      label: 'Herramientas', 
      children: [
        { icon: Package, label: 'Inventario', path: '/inventario' },
        { icon: Scissors, label: 'Servicios', path: '/servicios' },
        { icon: Beaker, label: 'Formulaciones', path: '/formulaciones' },
      ]
    },
    { 
      type: 'dropdown',
      icon: Briefcase, 
      label: 'Administrativo',
      badge: pendingAuthCount,
      children: administrativeChildren,
    },
    { 
      type: 'link',
      icon: Settings, 
      label: 'Configuración', 
      path: '/configuracion' 
    },
  ];

  // Barber/Stylist navigation - restricted view
  const barberNav = [
    { type: 'link', icon: Calendar, label: 'Agenda', path: '/agenda' },
    { type: 'link', icon: Receipt, label: 'Mis Comisiones', path: '/comisiones' },
  ];

  // Client navigation
  const clientNav = [
    { type: 'link', icon: User, label: 'Mi Cuenta', path: '/mi-cuenta' },
    { type: 'link', icon: Calendar, label: 'Mis Citas', path: '/mi-cuenta' },
    { type: 'link', icon: Gift, label: 'Mis Puntos', path: '/mi-cuenta' },
  ];

  if (userRole === 'admin' || userRole === 'master_admin') {
    return adminNav;
  } else if (userRole === 'barber' || userRole === 'stylist') {
    return barberNav;
  } else if (userRole === 'client') {
    return clientNav;
  }
  
  return adminNav;
};

// Navigation Item Component
const NavItem = ({ item, collapsed, isActive, openDropdowns, toggleDropdown }) => {
  const Icon = item.icon;
  const location = useLocation();
  
  if (item.type === 'dropdown') {
    const isOpen = openDropdowns[item.label];
    const hasActiveChild = item.children?.some(child => location.pathname === child.path);
    const showBadge = Number(item.badge) > 0;
    
    return (
      <Collapsible open={isOpen} onOpenChange={() => toggleDropdown(item.label)}>
        <CollapsibleTrigger asChild>
          <button
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-200 w-full",
              hasActiveChild
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
            data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
          >
            <Icon className={cn("w-5 h-5 flex-shrink-0", collapsed && "mx-auto")} />
            {!collapsed && (
              <>
                <span className="flex-1 text-left">{item.label}</span>
                {showBadge && (
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold px-1"
                    data-testid={`nav-badge-${item.label.toLowerCase()}`}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
                <ChevronRight className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  isOpen && "rotate-90"
                )} />
              </>
            )}
            {collapsed && showBadge && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
            )}
          </button>
        </CollapsibleTrigger>
        {!collapsed && (
          <CollapsibleContent className="pl-4 mt-1 space-y-1">
            {item.children?.map((child) => {
              const ChildIcon = child.icon;
              const childActive = location.pathname === child.path;
              const childBadge = Number(child.badge) > 0;
              
              return (
                <Link
                  key={child.path + child.label}
                  to={child.path}
                  data-testid={`nav-${child.path.slice(1)}`}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-all duration-200",
                    childActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  <ChildIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">{child.label}</span>
                  {childBadge && (
                    <span
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold px-1"
                      data-testid={`nav-badge-${child.path.slice(1)}`}
                    >
                      {child.badge > 99 ? '99+' : child.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </CollapsibleContent>
        )}
      </Collapsible>
    );
  }
  
  // Regular link
  return (
    <Link
      to={item.path}
      data-testid={`nav-${item.path.slice(1)}`}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-200",
        isActive
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      <Icon className={cn("w-5 h-5 flex-shrink-0", collapsed && "mx-auto")} />
      {!collapsed && <span>{item.label}</span>}
    </Link>
  );
};

export const Sidebar = ({ collapsed, onToggle }) => {
  const { user, selectedBusiness } = useAuth();
  const location = useLocation();
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [pendingAuthCount, setPendingAuthCount] = useState(0);
  
  const userRole = user?.is_master_admin ? 'master_admin' : user?.role || 'client';
  const canSeeAuthorizations = userRole === 'master_admin' || userRole === 'admin';

  useEffect(() => {
    if (!canSeeAuthorizations || !selectedBusiness) {
      setPendingAuthCount(0);
      return;
    }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await salesAPI.getPendingEditRequestsCount();
        if (!cancelled) setPendingAuthCount(data?.count || 0);
      } catch {
        // silent — badge is a nice-to-have
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [canSeeAuthorizations, selectedBusiness, location.pathname]);

  const navStructure = getNavStructure(userRole, pendingAuthCount);

  const toggleDropdown = (label) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  return (
    <aside 
      className={cn(
        "fixed right-0 top-0 z-40 h-screen border-l border-border bg-card transition-all duration-300 shadow-sm",
        collapsed ? "w-16" : "w-64"
      )}
      data-testid="sidebar-right"
    >
      <div className="flex h-full flex-col">
        {/* Logo & Business name */}
        <div className="flex h-16 items-center border-b border-border px-4 bg-muted/30">
          <div className={cn("flex items-center gap-2 py-2", collapsed ? "mx-auto" : "w-full")} data-testid="business-brand">
            <div className="w-8 h-8 rounded-md overflow-hidden bg-white border border-border flex items-center justify-center flex-shrink-0">
              <img src={BARRABAS_LOGO} alt="Logo" className="w-full h-full object-cover" />
            </div>
            {!collapsed && (
              <div className="flex-1 text-left overflow-hidden">
                <p className="text-sm font-semibold truncate text-foreground" data-testid="business-brand-name">{selectedBusiness?.name || 'Barrabás & JL Saloon'}</p>
                <p className="text-xs text-muted-foreground truncate">Barbería · Salón</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {navStructure.map((item) => (
              <NavItem
                key={item.label}
                item={item}
                collapsed={collapsed}
                isActive={location.pathname === item.path}
                openDropdowns={openDropdowns}
                toggleDropdown={toggleDropdown}
              />
            ))}
          </nav>
        </ScrollArea>

        {/* Toggle Button */}
        <div className="border-t border-border p-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="w-full hover:bg-muted"
            data-testid="sidebar-toggle"
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </aside>
  );
};

export const Header = ({ sidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pinDialogOpen, setPinDialogOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();

  return (
    <header 
      className={cn(
        "fixed top-0 left-0 z-30 h-16 border-b border-border bg-card/95 backdrop-blur transition-all duration-300",
        sidebarCollapsed ? "right-16" : "right-64"
      )}
    >
      <div className="flex h-full items-center justify-between px-6">
        {/* Search */}
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar clientes, servicios, productos..."
            className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid="global-search"
          />
        </div>

        <div className="flex items-center gap-4">
          {/* Notifications */}
          <Button variant="ghost" size="icon" className="relative hover:bg-muted" data-testid="notifications-btn">
            <Bell className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              3
            </span>
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 hover:bg-muted" data-testid="user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.avatar_url} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-foreground">{user?.first_name}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" data-testid="profile-link">
                Mi perfil
              </DropdownMenuItem>
              {!['barber', 'stylist'].includes(user?.role) && (
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/configuracion')} data-testid="config-menu-item">
                Configuración
              </DropdownMenuItem>
              )}
              {user?.is_master_admin && (
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setPinDialogOpen(true)}
                data-testid="change-pin-menu-item"
              >
                Cambiar mi PIN
              </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="cursor-pointer text-destructive focus:text-destructive"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ChangePinDialog
        open={pinDialogOpen}
        onOpenChange={setPinDialogOpen}
        requirePassword={!!user?.email}
      />
    </header>
  );
};

export const AppLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Warn the user via the browser's native confirmation dialog if they try to
  // close the tab while a cash register is still open for the day.
  useUnsavedCashRegisterGuard();

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main 
        className={cn(
          "pt-16 min-h-screen transition-all duration-300",
          sidebarCollapsed ? "mr-16" : "mr-64"
        )}
      >
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
